import {REGISTRY_SINGLETON} from '../../scenes/Registry';
import {selectGameResults, selectGameType} from '../reducers/sharedStateReducer/selectors';
import {
  selectIsCityCaptureAnimationPlaying,
  selectIsRuinsIncomeAnimationPending,
} from '../reducers/privateStateReducer/selectors';
import {selectIsRuinsIncomeAnimationDue} from '../selectors/modalVisibility';
import {cancelRuinsIncomeAnimationRequest} from '../actions';
import {GAME_TYPES} from '../../constants/game';
import {checkIfReplayRewinding} from '../../utils/replay/replayPlaybackSpeed';

// С этих действий запись матча начинается заново: перемотка к началу, новая
// запись или новая партия ботов на витрине меню
const RESTART_ACTIONS = ['LOAD_STATE', 'RESET_STATE', 'START_GAME'];

/**
 * Запись матча перемотали (или витрина меню начала новую партию): анимация
 * показывает уже не то поле. В живой партии полное состояние (ресинк,
 * переподключение) ее не обрывает: поле то же
 */
const checkIfOutdated = (state, scene) => {
  if (checkIfReplayRewinding()) {
    return true;
  }
  return Boolean(scene?.isDemoMode) || selectGameType(state) === GAME_TYPES.REPLAY;
};

/**
 * Витрина меню модалок не рисует, и флаг итогов раунда там никто не снимает:
 * ей незачем ждать очередь, только сам облет
 */
const checkIfQueueFree = (state, scene) => (
  scene?.isDemoMode ? !selectIsCityCaptureAnimationPlaying(state) : selectIsRuinsIncomeAnimationDue(state)
);

/**
 * Рост дохода руин на поле (см. scenes/RuinsIncomeAnimation). Запрос поднимает
 * modalsMiddleware на входе в первый раунд окна, а запускается анимация здесь —
 * как только дошла очередь: закрыты итоги раунда и все, что перед ними. Не
 * запустилась (нет сцены, скрытая вкладка, запись на большой скорости) — запрос
 * снимается, и руины просто начинают светиться.
 *
 * Песочницы ботов и сервер считают партии на своих сторах без этого
 * middleware — там анимации нет и быть не должно.
 */
export const createRuinsIncomeAnimationMiddleware = ({getGameScene}) => (
  (store) => (next) => (action) => {
    if (!action) {
      return next(action);
    }
    // Сцену спрашиваем только тогда, когда анимация может устареть или начаться:
    // middleware стоит на каждом экшене
    if (checkIfReplayRewinding() || RESTART_ACTIONS.includes(action.type)) {
      const scene = getGameScene();
      const state = store.getState();
      if (checkIfOutdated(state, scene)) {
        try {
          if (scene?.ruinsIncomeAnimation?.isActive) {
            scene.ruinsIncomeAnimation.abort({restoreCamera: true});
          }
          if (selectIsRuinsIncomeAnimationPending(state)) {
            store.dispatch(cancelRuinsIncomeAnimationRequest());
          }
        } catch (error) {
          console.warn('[RuinsIncome] не удалось оборвать анимацию', error);
        }
      }
    }

    const result = next(action);

    const state = store.getState();
    if (!selectIsRuinsIncomeAnimationPending(state)) {
      return result;
    }
    // Партия кончилась (досрочно, по отрыву): показывать незачем, а висящий запрос
    // прятал бы свечение и плашки руин на итоговом поле
    if (selectGameResults(state)) {
      store.dispatch(cancelRuinsIncomeAnimationRequest());
      return result;
    }
    const scene = getGameScene();
    if (!checkIfQueueFree(state, scene)) {
      return result;
    }

    let isStarted = false;
    try {
      isStarted = Boolean(scene?.ruinsIncomeAnimation?.start());
    } catch (error) {
      // Анимация — украшение: ее поломка не должна ронять диспатч
      console.warn('[RuinsIncome] не удалось запустить анимацию дохода руин', error);
    }
    if (!isStarted) {
      store.dispatch(cancelRuinsIncomeAnimationRequest());
    }
    return result;
  }
);

const ruinsIncomeAnimationMiddleware = createRuinsIncomeAnimationMiddleware({
  getGameScene: () => REGISTRY_SINGLETON.getGameScene?.(),
});

export default ruinsIncomeAnimationMiddleware;
